import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { contentService } from '../services/api';
import { brisbaneInputsToUtc, localDateKey, formatApptTime } from '../utils/dateFormatters';
import { STATUS_LABEL, pillarLabel, mondayKey, addDaysKey, dayLabel, whenLabel, seeMoreHook } from '../utils/content';
import { Modal } from '../components/Modal';
import PostEditor from '../components/PostEditor';
import PhotoLibrary from '../components/PhotoLibrary';
import PictureStudio from '../components/PictureStudio';
import './Content.css';

const TABS = [
  { key: 'week', label: 'Week' },
  { key: 'ideas', label: 'Ideas', statuses: ['idea'] },
  { key: 'drafts', label: 'Drafts', statuses: ['draft'] },
  { key: 'scheduled', label: 'Scheduled', statuses: ['scheduled', 'publishing'] },
  { key: 'sent', label: 'Posted', statuses: ['published'] },
  { key: 'photos', label: 'Photos' },
  { key: 'pictures', label: 'Pictures' },
  { key: 'voice', label: 'Voice' }
];

const BRIEFS = [
  'A good mix for the next fortnight',
  'Wet season is here: mould, slippery paths',
  'Get the place ready before Christmas visitors',
  'Introduce Andrew and Leanne: the two of us, two pressure washers',
  'Make a series out of our best job',
  'A few pure laughs, no selling'
];

/**
 * Content Studio (backlog #780, GID-Session-025): plan, write and schedule the
 * Get It Done Facebook posts, with the AI as creative partner. ?post=<id> opens
 * a post (the failure email links here), ?tab= picks the tab.
 */
export default function Content() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'week';
  const openId = Number(params.get('post')) || null;
  const [status, setStatus] = useState(null);
  const [posts, setPosts] = useState(null);
  const [week, setWeek] = useState(mondayKey());
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [p, s] = await Promise.all([contentService.posts(), contentService.status()]);
    if (p.success) setPosts(p.posts); else setError(p.error);
    if (s.success) setStatus(s);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setParam = (k, v) => setParams((prev) => {
    const n = new URLSearchParams(prev);
    if (v === null || v === undefined || v === '') n.delete(k); else n.set(k, String(v));
    return n;
  });
  const openPost = (id) => setParam('post', id);

  async function newPost(dayKey = null) {
    const body = { status: 'draft', title: '' };
    if (dayKey) body.scheduled_for = brisbaneInputsToUtc(dayKey, '07:30');
    const r = await contentService.create(body);
    if (!r.success) return setError(r.error);
    await load();
    openPost(r.post.id);
  }

  // "New post with these" from the library: a pair also gets its before/after image.
  async function newPostWith(libraryIds) {
    const r = await contentService.create({ status: 'draft', title: '', library_ids: libraryIds });
    if (!r.success) return setError(r.error);
    await load();
    openPost(r.post.id);
  }

  // "New post with this" from the picture studio: a joke post with the picture on it.
  async function newPostWithPicture(picture) {
    const r = await contentService.create({ status: 'draft', title: picture.title || '', pillar: 'joke', picture_id: picture.id });
    if (!r.success) return setError(r.error);
    await load();
    openPost(r.post.id);
  }

  const counts = useMemo(() => {
    const c = {};
    for (const t of TABS) if (t.statuses) c[t.key] = (posts || []).filter((p) => t.statuses.includes(p.status)).length;
    return c;
  }, [posts]);
  const failed = (posts || []).filter((p) => p.status === 'failed');
  const fb = status?.facebook;

  return (
    <div className="content-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Content</h1>
          <div className="content-status">
            {fb && (
              <span className={`content-pill ${fb.mode}`} title={fb.error || ''}>
                Facebook: {fb.mode === 'live' ? (fb.page?.name || 'live') : fb.mode === 'simulate' ? 'simulated on this computer' : 'not set up here'}
                {fb.error ? ' ⚠' : ''}
              </span>
            )}
            {status && <span className={`content-pill ${status.ai.ready ? 'live' : 'off'}`}>AI: {status.ai.ready ? 'ready' : 'not set up'}</span>}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline" disabled={!status?.ai.ready} onClick={() => setIdeasOpen(true)}>✨ Ideas with AI</button>
          <button type="button" className="btn btn-primary" onClick={() => newPost()}>+ New post</button>
        </div>
      </div>

      {error && <div className="form-error mb-md">{error}</div>}
      {failed.length > 0 && (
        <div className="content-failed">
          <strong>{failed.length === 1 ? "A post didn't go out." : `${failed.length} posts didn't go out.`}</strong>
          {failed.map((p) => <button key={p.id} type="button" className="btn-link" onClick={() => openPost(p.id)}>{p.title || `Post ${p.id}`}</button>)}
        </div>
      )}

      <div className="content-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`content-tab${tab === t.key ? ' on' : ''}`}
            onClick={() => setParam('tab', t.key === 'week' ? null : t.key)}>
            {t.label}{counts[t.key] ? <span className="content-tab-count">{counts[t.key]}</span> : null}
          </button>
        ))}
      </div>

      {!posts ? <div className="content-empty"><div className="loading-spinner"></div></div>
        : tab === 'week' ? <WeekView posts={posts} week={week} setWeek={setWeek} onOpen={openPost} onNew={newPost} />
        : tab === 'voice' ? <VoiceTab spend={status?.spend} ai={status?.ai} />
        : tab === 'photos' ? <PhotoLibrary aiReady={status?.ai.ready} onNewPost={newPostWith} />
        : tab === 'pictures' ? <PictureStudio status={status?.pictures} aiReady={status?.ai.ready} onNewPost={newPostWithPicture} />
        : <PostList posts={posts.filter((p) => TABS.find((t) => t.key === tab).statuses.includes(p.status))} tab={tab} onOpen={openPost}
            onIdeas={() => setIdeasOpen(true)} aiReady={status?.ai.ready} />}

      {ideasOpen && <IdeasDialog onClose={() => setIdeasOpen(false)} onDone={async () => { setIdeasOpen(false); await load(); setParam('tab', 'ideas'); }} />}
      {openId && <PostEditor key={openId} postId={openId} fbMode={fb?.mode} pictures={status?.pictures} aiReady={status?.ai.ready} onClose={() => { setParam('post', null); load(); }} onChanged={load} />}
    </div>
  );
}

function PostCard({ post, onOpen, compact = false }) {
  return (
    <button type="button" className={`post-card status-${post.status}${compact ? ' compact' : ''}`} onClick={() => onOpen(post.id)} data-post={post.id}>
      {post.first_media_id
        ? <img className="post-card-thumb" src={contentService.mediaUrl(post.first_media_id, 'thumb')} alt="" loading="lazy" />
        : <span className="post-card-thumb none">{post.media_count ? '' : 'Aa'}</span>}
      <span className="post-card-body">
        <span className="post-card-top">
          {post.scheduled_for && <span className="post-card-time">{compact ? formatApptTime(post.scheduled_for).toLowerCase() : whenLabel(post.scheduled_for)}</span>}
          <span className={`post-card-status status-${post.status}`}>{STATUS_LABEL[post.status]}{post.simulated ? ' (sim)' : ''}</span>
        </span>
        <span className="post-card-title">{post.title || seeMoreHook(post.caption) || 'Untitled'}</span>
        {!compact && (post.hook || post.caption) && <span className="post-card-hook text-muted">{post.hook || seeMoreHook(post.caption)}</span>}
        {!compact && (
          <span className="post-card-chips">
            {post.pillar && <span className="chip">{pillarLabel(post.pillar)}</span>}
            {post.job && <span className="chip">{post.job.job_number}{post.job.suburb ? ` · ${post.job.suburb}` : ''}</span>}
            {post.media_count > 0 && <span className="chip">{post.media_count} photo{post.media_count === 1 ? '' : 's'}</span>}
            {post.status === 'idea' && post.ai?.photo_ids?.length > 0 && <span className="chip">{post.ai.photo_ids.length} photo{post.ai.photo_ids.length === 1 ? '' : 's'} picked</span>}
            {post.ai?.shot_needed && <span className="chip chip-shot">📷 shot needed</span>}
            {post.approved_by_name && post.status === 'scheduled' && <span className="chip">OK'd by {post.approved_by_name}</span>}
          </span>
        )}
      </span>
    </button>
  );
}

function WeekView({ posts, week, setWeek, onOpen, onNew }) {
  const days = Array.from({ length: 7 }, (_, i) => addDaysKey(week, i));
  const today = localDateKey(new Date());
  const byDay = new Map(days.map((d) => [d, []]));
  for (const p of posts) {
    if (!p.scheduled_for) continue;
    const k = localDateKey(p.scheduled_for);
    if (byDay.has(k)) byDay.get(k).push(p);
  }
  const unplanned = posts.filter((p) => !p.scheduled_for && ['idea', 'draft'].includes(p.status));
  return (
    <div className="week">
      <div className="week-nav">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setWeek(addDaysKey(week, -7))}>◀ Earlier</button>
        <strong>{dayLabel(days[0])} – {dayLabel(days[6])}</strong>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setWeek(addDaysKey(week, 7))}>Later ▶</button>
        {week !== mondayKey() && <button type="button" className="btn btn-link btn-sm" onClick={() => setWeek(mondayKey())}>This week</button>}
      </div>
      <div className="week-grid">
        {days.map((d) => (
          <div key={d} className={`week-day${d === today ? ' today' : ''}${d < today ? ' past' : ''}`} data-day={d}>
            <div className="week-day-head">
              <span>{dayLabel(d)}</span>
              {d >= today && <button type="button" className="week-add" title="New post on this day (7:30 am)" onClick={() => onNew(d)}>+</button>}
            </div>
            {byDay.get(d).map((p) => <PostCard key={p.id} post={p} onOpen={onOpen} compact />)}
          </div>
        ))}
      </div>
      <p className="text-muted week-legend">Solid cards are approved and will go out on their own; dashed ones are ideas and drafts planned for that day.</p>
      {unplanned.length > 0 && (
        <div className="week-unplanned">
          <h3>Not given a day yet</h3>
          <div className="post-list">{unplanned.map((p) => <PostCard key={p.id} post={p} onOpen={onOpen} />)}</div>
        </div>
      )}
    </div>
  );
}

function PostList({ posts, tab, onOpen, onIdeas, aiReady }) {
  const sorted = tab === 'sent'
    ? [...posts].sort((a, b) => String(b.published_at).localeCompare(String(a.published_at)))
    : posts;
  if (!sorted.length) {
    return (
      <div className="content-empty">
        {tab === 'ideas' ? (
          <>
            <p>No ideas waiting. Ask the AI for a batch: it knows our jobs, our photos and the season.</p>
            <button type="button" className="btn btn-primary" disabled={!aiReady} onClick={onIdeas}>✨ Ideas with AI</button>
          </>
        ) : <p>{{ drafts: 'No drafts.', scheduled: 'Nothing scheduled. Approve a draft to put it here.', sent: 'Nothing posted from here yet.' }[tab]}</p>}
      </div>
    );
  }
  return <div className="post-list">{sorted.map((p) => <PostCard key={p.id} post={p} onOpen={onOpen} />)}</div>;
}

function IdeasDialog({ onClose, onDone }) {
  const [brief, setBrief] = useState('');
  const [count, setCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [started, setStarted] = useState(0);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [busy]);

  async function go() {
    setBusy(true); setError(''); setStarted(Date.now());
    const r = await contentService.pollAi(contentService.ideas(brief, count), { timeoutMs: 8 * 60 * 1000 });
    setBusy(false);
    if (!r.success) return setError(r.error);
    onDone(r.result);
  }

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title="✨ Ideas with AI" size="large">
      <div className="ideas-dialog">
        <p className="text-muted">The AI knows our voice, the jobs whose customers said yes (and what their photos show), what we've posted lately and the time of year. Tell it what you're after, or leave it blank for a good mix.</p>
        <div className="form-group">
          <label htmlFor="ideas-brief">What are we after?</label>
          <textarea id="ideas-brief" rows={3} value={brief} disabled={busy} onChange={(e) => setBrief(e.target.value)} placeholder="e.g. The Robina job as a 3-part series, plus something about wet-season mould" />
          <div className="pe-tweaks ideas-briefs">
            {BRIEFS.map((b) => <button key={b} type="button" className="pe-tweak" disabled={busy} onClick={() => setBrief(b)}>{b}</button>)}
          </div>
        </div>
        <div className="form-group ideas-count">
          <label htmlFor="ideas-count">How many?</label>
          <select id="ideas-count" value={count} disabled={busy} onChange={(e) => setCount(Number(e.target.value))}>
            {[3, 5, 8].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {busy && <p className="pe-ai-wait"><span className="loading-spinner small"></span> Thinking up ideas… {Math.round((Date.now() - started) / 1000)} s (a batch takes a minute or two)</p>}
        {error && <div className="form-error">{error}</div>}
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={go}>{busy ? 'Working…' : 'Come up with ideas'}</button>
      </div>
    </Modal>
  );
}

function VoiceTab({ spend, ai }) {
  const [voice, setVoice] = useState(null);
  const [text, setText] = useState('');
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    contentService.voice().then((r) => {
      if (!r.success) return setError(r.error);
      setVoice(r); setText(r.voice.text);
    });
  }, []);

  async function save(next) {
    const r = await contentService.saveVoice(next);
    if (!r.success) return setError(r.error);
    setVoice((v) => ({ ...v, voice: r.voice })); setText(r.voice.text);
    setSaved(r.voice.isDefault ? 'Back to the default.' : 'Saved. The AI uses it from the next request.');
    setTimeout(() => setSaved(''), 4000);
  }

  if (!voice) return error ? <div className="form-error">{error}</div> : <div className="content-empty"><div className="loading-spinner"></div></div>;
  return (
    <div className="voice-tab">
      <p className="text-muted">This is what the AI writes to: who we are, how we sound, what we joke about and what we never do. Change anything; it applies from the next request.</p>
      <textarea className="voice-text" rows={24} value={text} onChange={(e) => setText(e.target.value)} spellCheck />
      <div className="voice-actions">
        <button type="button" className="btn btn-primary" disabled={text === voice.voice.text} onClick={() => save(text)}>Save</button>
        {!voice.voice.isDefault && <button type="button" className="btn btn-secondary" onClick={() => save('')}>Reset to the default</button>}
        {saved && <span className="text-muted">{saved}</span>}
        {error && <span className="form-error">{error}</span>}
      </div>
      {spend && (
        <p className="text-muted voice-spend">
          AI this month: {spend.requests} request{spend.requests === 1 ? '' : 's'}, about US${spend.cost_usd.toFixed(2)}. Today {spend.today} of {spend.daily_cap}.
          {ai ? ` Writing: ${ai.creative_model}; photo checks: ${ai.triage_model}.` : ''}
        </p>
      )}
    </div>
  );
}
